import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Browser from '@wetransfer/concorde-browser';

import Form from './form';
import MobileUploader from './mobile-uploader';
import Aborted from './aborted';
import Progress from './progress';
import Complete from './complete';
import ReCaptcha from './recaptcha';
import EmailVerificationSuccess from './email-verification-success';
import EmailVerification from './email-verification';
import EmailVerificationResend from './email-verification-resend';
import AccountEmailVerificationFailed from './account-email-verification-failed';
import AccountEmailVerification from './account-email-verification';
import ForceAccountVerification from './force-account-verification';

import Actions from '@app/store/action';
import { STATUS } from '@constant/subscriptions';
import { isLoggedInPlusUser } from '@app/helper/user';
import { getIsForwardedTransfer } from '@app/transfer-bundle/store/selectors';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';

export class Uploader extends React.Component {
  // If this component gets mounted we'll need to make sure
  // there is a Transfer in the session, so if there isn't
  // one we'll create a new one.
  componentDidMount() {
    const {
      addRecipient,
      changeType,
      create,
      setMessage,
      route,
      transfer,
    } = this.props;
    // Do we already have a transfer in our session?
    if (transfer.state) {
      return;
    }

    create();

    const { to, msg } = route.query;
    if (to || msg) {
      if (to) {
        addRecipient(to);
      }
      if (msg) {
        setMessage(msg);
      }
      changeType('email');
    }
  }

  componentWillUnmount() {
    if (Browser.isMobile) {
      // On mobile, uses can essentially navigate away from the tranfser window.
      // So we want to clear any transfer notifications so if users navigate back
      // to the upload process they start clean, and no notification tries to render.
      this.props.clearTransferNotification();

      // For the account verification process, when the Uploader components unmount
      // we want to reset the state to VERIFY_ACCOUNT, which is the first step of the
      // account verification process, so users start clean if they navigate away
      // and then back to the upload process.
      if (this.props.user.loggedIn && !this.props.user.isVerified) {
        this.props.setState(TRANSFER_STATES.VERIFY_ACCOUNT);
      }
    }
  }

  // Now let's find out what state our Transfer is in and what
  // component we'll actually be rendering today.
  render() {
    // Get our current Transfer and it's state.
    const {
      transfer,
      user,
      account,
      isForwardedTransfer,
      route,
      create,
    } = this.props;
    if (!transfer.state) {
      return null;
    }
    let transferState = transfer.state;
    let component = null;

    // For forwards we don't display the spinner
    if (isForwardedTransfer && transfer.state === 'started') {
      transferState = 'init';
    }

    // If the storage is already full, we'll immediately show this message
    if (transferState === 'init' && user.loggedIn && user.storageFull) {
      transferState = 'aborted';
      transfer.lastError = 'LimitReached';
    }

    // If a plus user is logged in, make sure their account is still active.
    if (
      isLoggedInPlusUser(user) &&
      account.subscription &&
      !account.active_subscription
    ) {
      transferState = 'aborted';
      transfer.lastError = 'AccountInactive';

      if (account.subscription.status === STATUS.CANCELED) {
        transfer.lastError = 'AccountExpired';
      }
    }

    if (
      transfer.verification_required &&
      transfer.state !== TRANSFER_STATES.VERIFY_RESEND
    ) {
      transferState = 'verify';
    }

    switch (transferState) {
      // If we are not working on a transfer yet, or the transfer
      // has not yet started uploading - show the transfer form
      case 'init':
      default:
        component = Browser.isMobile ? MobileUploader : Form;
        break;

      // If we're not entirely sure if a transfer is spam
      case 'recaptcha':
        component = ReCaptcha;
        break;

      case TRANSFER_STATES.VERIFY:
        return (
          <EmailVerification
            prePopulatedEmail={transfer.sender}
            isLoggedIn={false}
          />
        );

      case TRANSFER_STATES.FORCE_ACCOUNT_VERIFICATION:
        return <ForceAccountVerification prePopulatedEmail={user.email} />;

      case TRANSFER_STATES.VERIFY_ACCOUNT:
        return <AccountEmailVerification prePopulatedEmail={user.email} />;

      case TRANSFER_STATES.VERIFY_ACCOUNT_FAILED:
        return <AccountEmailVerificationFailed />;

      case TRANSFER_STATES.VERIFY_RESEND:
        return (
          <EmailVerificationResend
            prePopulatedEmail={user.email || transfer.sender}
            isLoggedIn={user.loggedIn > 0}
            isVerifyingAccount={
              route.path === '/account-verification' && !!route.query.sig
            }
          />
        );

      // If it's started or paused, we'll show upload progress
      case 'started':
      case 'paused':
        component = Progress;
        break;

      case TRANSFER_STATES.VERIFY_ACCOUNT_SUCCESS:
        return <EmailVerificationSuccess create={create} user={user} />;

      // If it's aborted, something went wrong. Unless we want to add
      // a button to cancel, then we might need something else again.
      case 'aborted':
        component = Aborted;
        break;

      // It's finished!
      case 'finished':
        component = Complete;
        break;
    }

    // Render the component with needed props
    const TransferStateComponentTag = component;
    return (
      <TransferStateComponentTag
        transfer={transfer}
        transferType={transfer.type}
        storageLimit={user.rules.storageLimit}
        {...this.props}
      />
    );
  }
}

Uploader.propTypes = {
  account: PropTypes.object,
  addRecipient: PropTypes.func.isRequired,
  changeType: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  transfer: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  route: PropTypes.object.isRequired,
  setMessage: PropTypes.func.isRequired,
  user: PropTypes.object,
  notification: PropTypes.object,
  session: PropTypes.object,
  channel: PropTypes.object,
  isForwardedTransfer: PropTypes.bool,
  clearTransferNotification: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  account: state.account,
  layout: state.layout,
  transfer: state.transfer,
  route: state.route,
  session: state.session,
  user: state.user,
  notification: state.notification,
  channel: state.channel,
  isForwardedTransfer: getIsForwardedTransfer(state),
});

export default connect(
  mapStateToProps,
  {
    ...Actions.Transfer,
    clearTransferNotification: Actions.Notification.clearTransfer,
  },
  null,
  {
    pure: false,
  }
)(Uploader);
